/**
 * İstatistik raporlama ayrıştırıcısı.
 *
 * Desteklenen kalıplar (APA + Türkçe tez geleneği):
 *   t(48) = 2.41, p = .019          t(48)=2,41, p=,019        t = 2.41, p < .05
 *   F(1, 48) = 6.21, p = .016       F(1,48)=6,21, p=,016
 *   χ²(2, N = 120) = 8.42, p = .015
 *   r(48) = .42, p < .01            r = .42, n = 50, p = .003
 *   p < .0001                       p , .0001  (operatör kaybı)
 *
 * Her ifadeye confidence atanır. Karar kuralı:
 *   high   → açık istatistik adı + okunaklı değer + df/n + p
 *   medium → p eksik, df eksik veya virgüllü ondalık var
 *   low    → operatör kaybı, imkânsız değer, aşırı belirsizlik
 * Düşük güvenli ifadeler "hata" olarak raporlanmaz, "doğrulanamadı" olur.
 */

import type { Confidence, ConfidenceReasonCode, ParsedStat, StatFinding, StatKind } from '../types';
import type { DocumentIndex } from '../text/document';
import { compareP, recomputeP } from './pvalue';

export interface ParseOptions {
	alpha?: number;
	tolerance?: number;
}

interface Token {
	kind: StatKind;
	value: number;
	df1?: number;
	df2?: number;
	n?: number;
	start: number;
	end: number;
	raw: string;
	explicitParens: boolean;
}

interface PToken {
	op: 'lt' | 'eq' | 'gt';
	value: number;
	bounded: boolean;
	start: number;
	end: number;
	raw: string;
	/** Virgüllü ondalık mıydı? */
	decimalComma: boolean;
}

const NUM = String.raw`[-+]?(?:\d{1,3}(?:\.\d{3})*(?:[.,]\d+)?|[.,]\d+|\d+)`;

/** "2.41", ",05", "0,019" → sayı + virgül bayrağı */
export function parseNumber(s: string): { value: number; decimalComma: boolean } | null {
	const trimmed = s.trim();
	const decimalComma = /^\s*[-+]?(?:[.,]\d+|\d+[.,]\d+)\s*$/.test(trimmed) && trimmed.includes(',');
	const normalized = trimmed.replace(/\s/g, '').replace(/(\d)\.(\d{3})(?=\D|$)/g, '$1$2');
	const v = Number(normalized.replace(',', '.'));
	if (!Number.isFinite(v)) return null;
	return { value: v, decimalComma };
}

const STAT_PATTERNS: {
	kind: StatKind;
	re: RegExp;
	build: (m: RegExpExecArray) => {
		value?: number;
		df1?: number;
		df2?: number;
		n?: number;
		explicitParens: boolean;
	} | null;
}[] = [
	{
		kind: 'F',
		re: new RegExp(
			String.raw`(?<![\p{L}\p{N}_])F\s*(?:\(\s*(\d+)\s*[,;]\s*(\d+)\s*\))?\s*=\s*(${NUM})`,
			'gu'
		),
		build: (m) => ({
			value: parseNumber(m[3])?.value,
			df1: m[1] ? Number(m[1]) : undefined,
			df2: m[2] ? Number(m[2]) : undefined,
			explicitParens: Boolean(m[1])
		})
	},
	{
		kind: 't',
		re: new RegExp(
			String.raw`(?<![\p{L}\p{N}_])t\s*(?:\(\s*(\d+)\s*\))?\s*=\s*(${NUM})`,
			'gu'
		),
		build: (m) => ({
			value: parseNumber(m[2])?.value,
			df1: m[1] ? Number(m[1]) : undefined,
			explicitParens: Boolean(m[1])
		})
	},
	{
		kind: 'chi2',
		re: new RegExp(
			String.raw`(?:χ\s*²|χ2|X\s*²|X2|ki[\s-]?kare)\s*(?:\(\s*(\d+)\s*(?:[,;]\s*(?:N|n)\s*=\s*(\d+))?\s*\))?\s*=\s*(${NUM})`,
			'gu'
		),
		build: (m) => ({
			value: parseNumber(m[3])?.value,
			df1: m[1] ? Number(m[1]) : undefined,
			n: m[2] ? Number(m[2]) : undefined,
			explicitParens: Boolean(m[1])
		})
	},
	{
		kind: 'r',
		re: new RegExp(
			String.raw`(?<![\p{L}\p{N}_])r\s*(?:\(\s*(\d+)\s*\))?\s*=\s*(${NUM})`,
			'gu'
		),
		build: (m) => ({
			value: parseNumber(m[2])?.value,
			df1: m[1] ? Number(m[1]) : undefined,
			explicitParens: Boolean(m[1])
		})
	},
	{
		kind: 'z',
		re: new RegExp(
			String.raw`(?<![\p{L}\p{N}_])z\s*(?:\(\s*(\d+)\s*\))?\s*=\s*(${NUM})`,
			'gu'
		),
		build: (m) => ({
			value: parseNumber(m[2])?.value,
			df1: m[1] ? Number(m[1]) : undefined,
			explicitParens: Boolean(m[1])
		})
	}
];

/** p değerleri: "p = .03", "p<,001", "p , .0001", "p = 0.03" */
const P_RE = new RegExp(
	String.raw`(?<![\p{L}\p{N}_])p\s*([<>=])\s*(${NUM})`,
	'gu'
);

/** Operatör kaybı: "p , .0001" / "p ; .05" */
const P_BROKEN_RE = /(?<![\p{L}\p{N}_])p\s*([,;])\s*(\.\d+|\d+[.,]\d+)/gu;

export interface ParsedStats {
	stats: ParsedStat[];
	claims: {
		text: string;
		negated: boolean;
		hasP: boolean;
		start: number;
		end: number;
		confidence: Confidence;
	}[];
	/** p'si raporlanmamış istatistikler. */
	statsWithoutP: number;
	decimalCommaCount: number;
}

/**
 * Metni ayrıştırır. `index` verilirse offset'ler sayfa/bbox'a çözülür.
 */
export function parseStats(
	text: string,
	index?: DocumentIndex,
	options: ParseOptions = {}
): ParsedStats {
	const tokens: Token[] = [];
	const pTokens: PToken[] = [];

	for (const pattern of STAT_PATTERNS) {
		pattern.re.lastIndex = 0;
		let m: RegExpExecArray | null;
		while ((m = pattern.re.exec(text))) {
			const built = pattern.build(m);
			if (!built || built.value === undefined || !Number.isFinite(built.value)) continue;
			// Çakışan eşleşmeleri önle (ör. "F(1,48)=6.21" t kalıbına da uyabilir)
			const start = m.index;
			const end = start + m[0].length;
			if (tokens.some((t) => overlaps(t, { start, end }))) continue;
			tokens.push({
				kind: pattern.kind,
				value: built.value,
				df1: built.df1,
				df2: built.df2,
				n: built.n,
				start,
				end,
				raw: m[0].trim(),
				explicitParens: built.explicitParens
			});
		}
	}

	// p token'ları: önce bozuk, sonra normal; çakışma kontrolü ile
	P_BROKEN_RE.lastIndex = 0;
	let pm: RegExpExecArray | null;
	while ((pm = P_BROKEN_RE.exec(text))) {
		const parsed = parseNumber(pm[2]);
		if (!parsed || parsed.value < 0 || parsed.value > 1) continue;
		pTokens.push({
			op: 'lt',
			value: parsed.value,
			bounded: false,
			start: pm.index,
			end: pm.index + pm[0].length,
			raw: pm[0].trim(),
			decimalComma: true
		});
	}

	P_RE.lastIndex = 0;
	while ((pm = P_RE.exec(text))) {
		const parsed = parseNumber(pm[2]);
		if (!parsed || parsed.value < 0 || parsed.value > 1.0001) continue;
		const start = pm.index;
		const end = start + pm[0].length;
		if (pTokens.some((t) => overlaps(t, { start, end }))) continue;
		pTokens.push({
			op: pm[1] === '<' ? 'lt' : pm[1] === '>' ? 'gt' : 'eq',
			value: Math.min(1, parsed.value),
			bounded: pm[1] === '<' && parsed.value <= 0.001,
			start,
			end,
			raw: pm[0].trim(),
			decimalComma: parsed.decimalComma
		});
	}

	const stats: ParsedStat[] = [];
	let decimalCommaCount = 0;

	for (const token of tokens) {
		// Virgüllü ondalık kontrolü: "2,41" evet; "F(1,48)" hayır
		const rawHasComma = /\d,\d/.test(token.raw) && !/\(\s*\d+\s*,\s*\d+\s*\)/.test(token.raw);
		if (rawHasComma) decimalCommaCount++;

		// p eşleştirme: aynı parantez/cümle içinde en yakın p
		const sentenceEnd = findClauseEnd(text, token.end);
		const nearP = pTokens
			.filter((p) => p.start >= token.end - 1 && p.start < sentenceEnd && p.start - token.end < 60)
			.sort((a, b) => a.start - b.start)[0];

		// n eşleştirme (r için): "n = 50" yakında
		let n = token.n;
		if (n === undefined && token.kind === 'r') {
			const nm = /(?<![\p{L}])n\s*=\s*(\d+)/u.exec(
				text.slice(token.end, Math.min(text.length, token.end + 80))
			);
			if (nm) n = Number(nm[1]);
		}

		const location = index ? index.locationAt(token.start, token.end) : { charStart: token.start, charEnd: token.end };

		// Güven değerlendirmesi
		const reasons: string[] = [];
		const reasonCodes: ConfidenceReasonCode[] = [];
		let confidence: Confidence = 'high';
		const downgrade = (to: Confidence, code: ConfidenceReasonCode, reason: string) => {
			const rank = { high: 0, medium: 1, low: 2 } as const;
			if (rank[to] > rank[confidence]) confidence = to;
			reasons.push(reason);
			reasonCodes.push(code);
		};

		if (rawHasComma) downgrade('medium', 'decimalComma', 'Ondalık ayırıcı virgülle yazılmış; Türkçe biçim.');
		if (!nearP) downgrade('medium', 'pMissing', 'Aynı ifadede raporlanmış p değeri bulunamadı.');
		if (token.kind === 't' && !token.df1)
			downgrade('medium', 'dfMissingT', 't için serbestlik derecesi yok; p yeniden hesaplanamaz.');
		if (token.kind === 'F' && (!token.df1 || !token.df2))
			downgrade('medium', 'dfMissingF', 'F için serbestlik dereceleri eksik.');
		if (token.kind === 'chi2' && !token.df1)
			downgrade('medium', 'dfMissingChi', 'χ² için serbestlik derecesi yok.');
		if (token.kind === 'r' && !n && !token.df1)
			downgrade('medium', 'nMissing', 'r için örneklem büyüklüğü yok.');
		if (token.kind === 't' && token.value < 0)
			downgrade('medium', 'negativeT', 'Negatif t değeri; deney/karşılaştırma yönü belirsiz.');

		// İmkânsız değerler
		let impossible: string | undefined;
		if (token.kind === 'r' && Math.abs(token.value) > 1) impossible = 'r değeri ±1 aralığının dışında.';
		if (token.kind === 'F' && token.value < 0) impossible = 'F değeri negatif olamaz.';
		if (token.kind === 'chi2' && token.value < 0) impossible = 'χ² değeri negatif olamaz.';
		if (token.kind === 'p' && (token.value < 0 || token.value > 1)) impossible = 'p değeri 0-1 aralığı dışında.';
		if (impossible) downgrade('low', 'impossible', impossible);

		if (nearP) {
			if (/[,;]/.test(nearP.raw) && nearP.op === 'lt' && nearP.decimalComma) {
				downgrade('low', 'operatorLoss', 'p ifadesinde operatör kaybı olabilir ("p , .0001").');
			}
			if (nearP.value > 1) downgrade('low', 'pOverOne', 'p değeri 1’den büyük okundu.');
		}

		const stat: ParsedStat = {
			id: `s${stats.length + 1}`,
			kind: token.kind,
			raw: token.raw + (nearP ? `, ${nearP.raw}` : ''),
			value: token.value,
			decimalComma: rawHasComma,
			df1: token.df1,
			df2: token.df2,
			n,
			p: nearP?.value,
			pOp: nearP?.op,
			pBound: nearP?.bounded,
			pRaw: nearP?.raw,
			tails: 2,
			location,
			confidence,
			confidenceReasons: reasons,
			confidenceReasonCodes: reasonCodes,
			context: contextAround(text, token.start, token.end),
			globalStart: token.start,
			pStart: nearP?.start
		};
		stats.push(stat);
	}

	return {
		stats,
		claims: extractSignificanceClaims(text),
		statsWithoutP: stats.filter((s) => s.p === undefined).length,
		decimalCommaCount
	};
}

/** Parentezsiz, yalnızca p raporlayan ifadeler için ayrı tarama. */
export function findStandalonePs(text: string): { raw: string; value: number; start: number; end: number }[] {
	const out: { raw: string; value: number; start: number; end: number }[] = [];
	const re = new RegExp(String.raw`(?<![\p{L}\p{N}_])p\s*([<>=])\s*(${NUM})`, 'gu');
	let m: RegExpExecArray | null;
	while ((m = re.exec(text))) {
		const parsed = parseNumber(m[2]);
		if (!parsed || parsed.value < 0 || parsed.value > 1) continue;
		out.push({ raw: m[0], value: parsed.value, start: m.index, end: m.index + m[0].length });
	}
	return out;
}

const SIGNIFICANCE_RE =
	/(istatistiksel(?: olarak)?\s+(?:anlamlı|önemli)|(?:anlamlı|manidar)(?:\s+bir)?\s+(?:fark|ilişki|korelasyon|etki|farklılık|düzeyde)|statistically\s+significant|significant\s+(?:difference|relationship|correlation|effect))/giu;

const NEGATION_RE =
	/(anlamlı\s+değil|anlamsız|manidar\s+değil|not\s+significant|no\s+significant|(?:fark|ilişki|etki|korelasyon)\s+(?:yok|yoktur|bulunmamıştır|saptanmamıştır))/iu;

function extractSignificanceClaims(text: string): ParsedStats['claims'] {
	const out: ParsedStats['claims'] = [];
	SIGNIFICANCE_RE.lastIndex = 0;
	let m: RegExpExecArray | null;
	while ((m = SIGNIFICANCE_RE.exec(text))) {
		const start = m.index;
		const end = start + m[0].length;
		// Cümle sınırları
		const sentenceStart = Math.max(text.lastIndexOf('.', start) + 1, text.lastIndexOf('\n', start) + 1);
		const sentenceEnd = firstSentenceEnd(text, end);
		const sentence = text.slice(sentenceStart, sentenceEnd);
		const negated = NEGATION_RE.test(sentence);
		const hasP = /(?<![\p{L}\p{N}_])p\s*[<>=,;]\s*[.,]?\d/iu.test(sentence);
		let confidence: Confidence = 'high';
		if (!hasP) confidence = 'medium';
		if (negated) confidence = 'low';
		out.push({ text: m[0], negated, hasP, start, end, confidence });
	}
	return out;
}

function firstSentenceEnd(text: string, from: number): number {
	for (let i = from; i < text.length; i++) {
		if (text[i] === '.' || text[i] === '!' || text[i] === '?' || text[i] === '\n') {
			// Ondalık nokta / başta ondalık ("p = .019") cümle sonu değildir
			if (text[i] === '.' && (/\d/.test(text[i - 1] ?? '') || /\d/.test(text[i + 1] ?? ''))) continue;
			return i + 1;
		}
	}
	return text.length;
}

function findClauseEnd(text: string, from: number): number {
	const stop = ['.', ';', ']', ')', '\n'];
	let end = text.length;
	for (const ch of stop) {
		const idx = text.indexOf(ch, from);
		if (idx !== -1 && idx < end) end = idx;
	}
	// Nokta ondalık ayırıcı ise yok say
	return Math.max(from, end);
}

function contextAround(text: string, start: number, end: number): string {
	const from = Math.max(0, text.lastIndexOf('.', start) + 1, text.lastIndexOf('\n', start) + 1);
	let to = firstSentenceEnd(text, end);
	if (to - from > 320) to = Math.min(text.length, start + 200);
	return text.slice(from, to).replace(/\s+/g, ' ').trim();
}

function overlaps(a: { start: number; end: number }, b: { start: number; end: number }): boolean {
	return a.start < b.end && b.start < a.end;
}

/**
 * Ayrıştırılan istatistikleri yeniden hesaplama ve tutarlılık kontrolleriyle
 * zenginleştirir.
 */
export function analyzeStats(
	parsed: ParsedStats,
	options: ParseOptions = {}
): { findings: StatFinding[]; issues: ParsedStat[] } {
	const findings: StatFinding[] = [];
	const issues: ParsedStat[] = [];

	for (const stat of parsed.stats) {
		const rec = recomputeP({
			kind: stat.kind as RecomputeInputKind,
			value: stat.value,
			df1: stat.df1,
			df2: stat.df2,
			n: stat.n,
			tails: stat.tails
		});

		const finding: StatFinding = {
			stat,
			severity: 'ok'
		};

		if (!rec.ok) {
			finding.skipReason = rec.reason;
			finding.severity = stat.confidence === 'low' ? 'info' : 'ok';
			findings.push(finding);
			continue;
		}

		finding.recomputedP = rec.result.p;

		if (stat.p !== undefined) {
			const cmp = compareP(stat.p, rec.result.p, {
				alpha: options.alpha ?? 0.05,
				tolerance: options.tolerance ?? 0.3
			});
			finding.delta = rec.result.p - stat.p;
			finding.consistent = cmp.consistent;
			finding.thresholdFlip = cmp.thresholdFlip;

			if (stat.confidence === 'low') {
				finding.severity = cmp.thresholdFlip ? 'warning' : 'info';
				if (cmp.thresholdFlip) {
					finding.message =
						'Düşük güvenli ifade: raporlanan ve yeniden hesaplanan p eşiğin farklı tarafında, ancak çıkarım güvenilir değil.';
				}
			} else if (cmp.thresholdFlip) {
				finding.severity = 'critical';
				finding.message = `Anlamlılık kararı çelişiyor: raporlanan p = ${fmtP(stat.p)}, istatistikten hesaplanan p = ${fmtP(rec.result.p)}.`;
			} else if (!cmp.consistent) {
				finding.severity = 'warning';
				finding.message = `p değeri istatistikle tam tutmuyor: raporlanan ${fmtP(stat.p)}, hesaplanan ${fmtP(rec.result.p)}.`;
			}
		} else {
			finding.severity = 'info';
			finding.message = 'İstatistik raporlanmış ancak p değeri verilmemiş.';
		}

		findings.push(finding);
		if (finding.severity === 'critical' || finding.severity === 'warning') issues.push(stat);
	}

	return { findings, issues };
}

type RecomputeInputKind = 't' | 'F' | 'chi2' | 'r' | 'z';

function fmtP(p: number): string {
	if (p < 0.001) return '< .001';
	return p.toFixed(3).replace(/^0/, '');
}
